import { captureException } from '@sentry/nextjs';
import { AxiosError } from 'axios';
import Breadcrumb from 'components/breadcrumb/breadcrumb-component';
import NextImage from 'components/image/image';
import LayoutComponent from 'components/layout/layout-component';
import { NewsletterHorizontal } from 'components/newsletter/newsletter-horizontal-component';
import { createPost } from 'components/post/post-helpers';
import { PostType } from 'components/post/post-types';
import ReactSquadModalAd from 'components/reactsquadad/react-squad-modal-ad';
import RelatedPosts from 'components/related-posts/related-posts-component';
import SidebarComponent from 'components/sidebar/sidebar-component';
import { Typography } from 'components/typography/typography-component';
import { htmlToText } from 'html-to-text';
import { findCategory, getAllCategories, getCategory } from 'lib/db/category';
import {
  findPost,
  getAllPosts,
  getCategoryRelatedPost,
  getPost,
} from 'lib/db/post';
import { Category, Post } from 'lib/db/types';
import { sanitizeContent } from 'lib/utils/sanitize-content';
import { shuffle } from 'lib/utils/shuffle';
import { createPostUrls } from 'lib/utils/slugs';
import { stripHtmlContent } from 'lib/utils/strip-html';
import { GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

const SHORT_POST_MAX_WORDS_COUNT = 10_000;

export default function PostPage({
  post,
  posts,
  category,
  canonicalUrl,
  featuredImage,
}: {
  post: Post;
  posts: PostType[];
  category: Category;
  canonicalUrl: string;
  featuredImage: string;
}) {
  if (!post) {
    return null;
  }

  if (!post) {
    return;
  }

  const { title, content, excerpt, yoast_head_json: seo } = post || {};
  const paragraph = sanitizeContent(content.rendered);
  const isShortPost =
    stripHtmlContent(paragraph).split(' ').length <= SHORT_POST_MAX_WORDS_COUNT;
  const newExcerpt = sanitizeContent(`${excerpt.rendered}`);
  const metaDesc = stripHtmlContent(newExcerpt);
  const postTitle = stripHtmlContent(title.rendered);

  const SEO = {
    title: htmlToText(stripHtmlContent(seo?.title ?? '') || postTitle || ''),
    description: htmlToText(seo?.description || metaDesc || ''),
    openGraph: {
      type: seo?.og_type || '',
      url: canonicalUrl,
      siteName: seo?.og_site_name || '',
      title: htmlToText(seo?.og_title || postTitle || ''),
      description: htmlToText(seo?.og_description || metaDesc || ''),
      images: [
        {
          url: seo?.og_image?.[0]?.url || '',
          alt: postTitle || '',
        },
      ],
    },
    canonicalUrl,
  };

  return (
    <LayoutComponent>
      <NextSeo {...SEO} />
      <div className="mx-auto mt-10 flex w-full flex-col py-5 pb-9 align-top md:container lg:flex-row">
        <div className="mx-auto px-4 sm:w-full lg:w-[85ch]">
          <Breadcrumb
            pages={[
              { name: 'EarlyNode', href: '/', current: false },
              ...(category
                ? [
                    {
                      name: category.name,
                      href: `/${category.slug}`,
                      current: false,
                    },
                  ]
                : []),
              {
                name: postTitle || '',
                href: `/${category.slug}/${post.slug}`,
                current: true,
              },
            ]}
          />

          <article className="prose relative w-full max-w-full py-16 prose-headings:font-title">
            <Typography variant="h1" className="text-3xl md:text-5xl">
              {postTitle}
            </Typography>
            {featuredImage && (
              <div className="relative flex h-80 w-full items-center justify-center rounded px-6">
                <NextImage
                  useSkeleton
                  src={featuredImage}
                  alt="featured post"
                  className="my-2 h-full w-full"
                  fill
                  objectFit="contain"
                  unoptimized
                />
              </div>
            )}
            <section
              className="mb-10"
              dangerouslySetInnerHTML={{ __html: paragraph }}
            />
            {/* <NewsletterHorizontal
              title="A weekly dose of insights to grow your SaaS."
              placeholderText="Enter your email"
              buttonText="Join Now"
              className="mb-12 font-bold"
            /> */}

            <RelatedPosts posts={shuffle(posts ?? [])} />
          </article>
        </div>

        {isShortPost && (
          <div className="w-full lg:w-1/3">
            <SidebarComponent
              categorySlug={category?.slug ?? ''}
              postTitle={`Newest Articles of ${category?.name}`}
              posts={posts ?? []}
            />
          </div>
        )}
        <ReactSquadModalAd />
      </div>
    </LayoutComponent>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const [posts, categories] = await Promise.all([
    getAllPosts(),
    getAllCategories(),
  ]);

  const paths = createPostUrls(posts, categories);

  return {
    paths,
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { category, post: postSlug } = params || {};

  if (!category || !postSlug) {
    return { notFound: true };
  }

  try {
    const [categories, posts] = await Promise.all([
      findCategory({ slug: category as string }),
      findPost({ slug: postSlug as string }),
    ]);

    if (categories?.length === 0) {
      return { notFound: true };
    }
    const foundCategory = categories[0];

    if (posts.length === 0) {
      return { notFound: true };
    }
    const post = posts[0];

    if (!post) {
      return { notFound: true };
    }

    const relatedPosts = await getCategoryRelatedPost({
      postId: post.id,
      categoryId: foundCategory.id,
    });

    return {
      props: {
        canonicalUrl: `${process.env.NEXT_PUBLIC_SITE_URL}/${foundCategory.slug}/${postSlug}`,
        featuredImage: post?.featured_image_url ?? '',
        posts: relatedPosts.map(createPost),
        post: {
          title: post.title,
          content: post.content,
          excerpt: post.excerpt,
          slug: post.slug,
          display_ad_type: post?.acf?.ad_type ?? '',
          yoast_head_json: post?.yoast_head_json,
          featured_media_url: post?.featured_image_url ?? '',
        },
        category: {
          name: foundCategory.name,
          description: foundCategory.description,
          slug: foundCategory.slug,
        },
      },
      revalidate: 600,
    };
  } catch (error) {
    captureException(error, {
      extra: {
        path: `getStaticProps: /${category}/${postSlug}`,
        apiError: error instanceof AxiosError ? error?.response?.data : false,
      },
    });
    return { notFound: true };
  }
};
